import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import useContract from "../../hooks/useContract";

import {
  Page,
  Panel,
  shortAddress,
  formatRaw,
  formatInteger,
  getError,
  USD_DECIMALS,
} from "./ktlShared";

/*
  KP tier rules:

  KP < 10       => Not qualified yet
  KP 10 - 39    => Tier 1
  KP 40         => Tier 2
  KP > 40       => Tier 3
*/

const getTierByKP = (kp) => {
  const parsedKP = Number(
    kp?.toString?.() || kp || 0
  );

  if (parsedKP < 10) {
    return {
      label: "Not qualified yet",
      className:
        "border-white/10 bg-white/5 text-[#777]",
    };
  }

  if (parsedKP < 40) {
    return {
      label: "Tier 1",
      className:
        "border-blue-400/30 bg-blue-400/10 text-blue-300",
    };
  }

  if (parsedKP === 40) {
    return {
      label: "Tier 2",
      className:
        "border-purple-400/30 bg-purple-400/10 text-purple-300",
    };
  }

  return {
    label: "Tier 3",
    className:
      "border-[#ffcc66]/30 bg-[#ffcc66]/10 text-[#ffcc66]",
  };
};

const Leaderboard = () => {
  const {
    getCurrentLeagueId,
    getLeague,
    getLeaderboard,
    getRewardTokenData,
  } = useContract();

  const [currentLeagueId, setCurrentLeagueId] =
    useState(0);

  const [selectedLeagueId, setSelectedLeagueId] =
    useState(0);

  const [league, setLeague] = useState(null);
  const [rows, setRows] = useState([]);

  const [token, setToken] = useState({
    symbol: "KIND",
    decimals: 18,
  });

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] =
    useState(true);

  const [error, setError] = useState("");

  /*
    Build available league options
  */

  const leagueOptions = useMemo(() => {
    const options = [];

    for (
      let id = Number(currentLeagueId);
      id >= 1;
      id -= 1
    ) {
      options.push({
        id,
        label:
          id === Number(currentLeagueId)
            ? `League #${id} — Current`
            : `League #${id}`,
      });
    }

    return options;
  }, [currentLeagueId]);

  const isCurrentLeague =
    Number(selectedLeagueId) ===
    Number(currentLeagueId);

  /*
    Load current league and reward token
  */

  const loadInitialData = useCallback(async () => {
    try {
      setInitialLoading(true);
      setError("");

      const [
        currentLeagueIdRaw,
        rewardToken,
      ] = await Promise.all([
        getCurrentLeagueId(),
        getRewardTokenData(),
      ]);

      const parsedCurrentLeagueId = Number(
        currentLeagueIdRaw?.toString?.() ||
          currentLeagueIdRaw ||
          0
      );

      setCurrentLeagueId(parsedCurrentLeagueId);
      setSelectedLeagueId(parsedCurrentLeagueId);

      if (rewardToken) {
        setToken(rewardToken);
      }
    } catch (error) {
      console.error(
        "loadInitialData:",
        error
      );

      setError(getError(error));
    } finally {
      setInitialLoading(false);
    }
  }, [
    getCurrentLeagueId,
    getRewardTokenData,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /*
    Load selected league leaderboard
  */

  const loadLeaderboard = useCallback(async () => {
    if (!selectedLeagueId) {
      setLeague(null);
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const selectedLeague = await getLeague(
        selectedLeagueId
      );

      if (!selectedLeague) {
        throw new Error(
          `League #${selectedLeagueId} was not found`
        );
      }

      const visibleSize = Number(
        selectedLeague?.visibleLeaderboardSize ||
          50
      );

      const leaderboardRows =
        await getLeaderboard(
          selectedLeagueId,
          visibleSize
        );

      setLeague(selectedLeague);

      setRows(
        Array.isArray(leaderboardRows)
          ? leaderboardRows
          : []
      );
    } catch (error) {
      console.error(
        "loadLeaderboard:",
        error
      );

      setError(getError(error));
      setLeague(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    getLeaderboard,
    getLeague,
    selectedLeagueId,
  ]);

  useEffect(() => {
    if (
      !initialLoading &&
      selectedLeagueId
    ) {
      loadLeaderboard();
    }
  }, [
    initialLoading,
    loadLeaderboard,
    selectedLeagueId,
  ]);

  /*
    Search/filter leaderboard rows
  */

  const filtered = useMemo(() => {
    const query = search
      .toLowerCase()
      .trim();

    if (!query) {
      return rows;
    }

    return rows.filter((row) => {
      const wallet = String(
        row?.address || ""
      ).toLowerCase();

      const username = String(
        row?.username || "Anon"
      ).toLowerCase();

      const rank = String(
        row?.rank || ""
      );

      return (
        wallet.includes(query) ||
        username.includes(query) ||
        rank === query.replace("#", "")
      );
    });
  }, [rows, search]);

  /*
    League status
  */

  const leagueStatus = useMemo(() => {
    if (!league) {
      return {
        label: "NOT FOUND",
        className:
          "border-white/10 bg-white/5 text-[#777]",
      };
    }

    if (league.active) {
      return {
        label: "ACTIVE",
        className:
          "border-green-400/30 bg-green-400/10 text-green-300",
      };
    }

    if (league.finalized) {
      return {
        label: "FINALIZED",
        className:
          "border-blue-400/30 bg-blue-400/10 text-blue-300",
      };
    }

    return {
      label: "INACTIVE",
      className:
        "border-white/10 bg-white/5 text-[#777]",
    };
  }, [league]);

  /*
    Render
  */

  return (
    <Page
      eyebrow="Public rankings"
      title="Leaderboard"
      description="Select any league to view current or historical rankings."
      action={
        <button
          type="button"
          onClick={loadLeaderboard}
          disabled={
            loading ||
            initialLoading ||
            !selectedLeagueId
          }
          className="rounded-lg border border-[#ffcc66]/25 px-4 py-2 text-xs font-bold text-[#ffcc66] transition hover:bg-[#ffcc66]/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading
            ? "Refreshing..."
            : "Refresh"}
        </button>
      }
    >
      {error && (
        <div className="mb-5 rounded-xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* League selector */}

      <Panel className="mb-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#777]">
              League history
            </p>

            <h2 className="mt-2 text-xl font-black">
              Select League
            </h2>

            <p className="mt-1 text-sm text-[#777]">
              Select the current league or any previous finalized league.
            </p>
          </div>

          <div className="w-full md:w-[280px]">
            <label className="mb-2 block text-xs text-[#777]">
              League
            </label>

            <select
              value={selectedLeagueId}
              onChange={(event) => {
                setSelectedLeagueId(
                  Number(event.target.value)
                );

                setSearch("");
              }}
              disabled={
                initialLoading ||
                leagueOptions.length === 0
              }
              className="h-12 w-full rounded-xl border border-white/10 bg-black/30 px-4 text-sm font-bold text-white outline-none transition focus:border-[#ffcc66]/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {leagueOptions.length === 0 ? (
                <option value="0">
                  No leagues available
                </option>
              ) : (
                leagueOptions.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                  >
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
      </Panel>

      {/* League overview */}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Panel>
          <p className="text-xs text-[#777]">
            League
          </p>

          <div className="mt-2 flex items-center gap-2">
            <p className="text-xl font-black text-[#ffcc66]">
              #{selectedLeagueId || "0"}
            </p>

            {isCurrentLeague && (
              <span className="rounded-full border border-[#ffcc66]/25 bg-[#ffcc66]/10 px-2 py-1 text-[8px] font-bold text-[#ffcc66]">
                CURRENT
              </span>
            )}
          </div>
        </Panel>

        <Panel>
          <p className="text-xs text-[#777]">
            Status
          </p>

          <div className="mt-2">
            <span
              className={`inline-flex rounded-full border px-3 py-1 text-[9px] font-bold ${leagueStatus.className}`}
            >
              {leagueStatus.label}
            </span>
          </div>
        </Panel>

        <Panel>
          <p className="text-xs text-[#777]">
            Visible Wallets
          </p>

          <p className="mt-2 text-xl font-black">
            {formatInteger(
              league?.visibleLeaderboardSize
            )}
          </p>
        </Panel>

        <Panel>
          <p className="text-xs text-[#777]">
            Qualified Wallets
          </p>

          <p className="mt-2 text-xl font-black">
            {formatInteger(
              league?.totalQualifiedWallets
            )}
          </p>
        </Panel>
      </div>

      {/* Search and table */}

      <Panel className="overflow-hidden p-0">
        <div className="flex flex-col gap-4 border-b border-white/[0.06] p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#ffcc66]">
              League #{selectedLeagueId}
            </p>

            <h2 className="mt-1 text-xl font-black">
              Rankings
            </h2>

            <p className="mt-1 text-xs text-[#777]">
              Ranked by KP, then volume, then wallet address.
            </p>
          </div>

          <div className="w-full md:max-w-[380px]">
            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search username, wallet or rank"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm outline-none transition focus:border-[#ffcc66]/40"
            />
          </div>
        </div>

        {loading || initialLoading ? (
          <div className="p-12 text-center">
            <p className="font-bold text-white">
              Loading leaderboard...
            </p>

            <p className="mt-2 text-sm text-[#666]">
              Reading League #
              {selectedLeagueId || "..."} from the blockchain.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-black/25 text-left text-[9px] uppercase tracking-[0.15em] text-[#777]">
                <tr>
                  <th className="px-5 py-4">
                    Rank
                  </th>

                  <th className="px-5 py-4">
                    Username
                  </th>

                  <th className="px-5 py-4">
                    Wallet
                  </th>

                  <th className="px-5 py-4">
                    Tier
                  </th>

                  <th className="px-5 py-4">
                    KP
                  </th>

                  <th className="px-5 py-4">
                    Volume
                  </th>

                  <th className="px-5 py-4">
                    Reward
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.length ? (
                  filtered.map((row) => {
                    const tier = getTierByKP(row.kp);

                    return (
                      <tr
                        key={row.address}
                        className="border-t border-white/[0.05] text-sm transition hover:bg-white/[0.025]"
                      >
                        <td className="px-5 py-4 font-black text-[#ffcc66]">
                          #{row.rank}
                        </td>

                        <td className="px-5 py-4 font-bold">
                          {row.username || "Anon"}
                        </td>

                        <td className="px-5 py-4 font-mono text-xs text-[#888]">
                          {shortAddress(row.address)}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full border px-3 py-1 text-[9px] font-bold ${tier.className}`}
                          >
                            {tier.label}
                          </span>
                        </td>

                        <td className="px-5 py-4 font-bold">
                          {formatInteger(row.kp)} KP
                        </td>

                        <td className="px-5 py-4">
                          $
                          {formatRaw(
                            row.volumeUsd,
                            USD_DECIMALS
                          )}
                        </td>

                        <td className="px-5 py-4 font-bold text-[#ffcc66]">
                          {formatRaw(
                            row.estimatedReward,
                            token.decimals,
                            4
                          )}{" "}
                          {token.symbol}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="p-10 text-center text-[#666]"
                    >
                      {search
                        ? "No matching participants found."
                        : `No participants found for League #${selectedLeagueId}.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </Page>
  );
};

export default Leaderboard;